import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiWritable,
  FunnelInductionStatus,
} from "@repo/app-types/entities/funnel";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import { IconSettings } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { useNavigate } from "react-router";
import {
  useFunnelQuery,
  useVoiceAgentFunnelsQuery,
} from "../../api/entities/funnel.js";
import { appPaths } from "../../app/appPaths.js";
import { useFeatureFlagVariant } from "../../helpers/featureFlags/hooks.js";
import { FunnelSettingsModal } from "./funnel/funnelSettingsModal.js";
import type { FunnelView } from "./funnel/useFunnelView.js";

export type PerformanceView = "funnel" | "analytics";

export interface PerformanceToolbarProps {
  teammateAgentId: TeammateId;
  view: PerformanceView;
  funnelView: FunnelView;
}

/**
 * The Performance tab's toolbar, rendered by the agent page so it stays
 * mounted across the tab's views: the Funnel / Analytics toggle, and on the
 * Funnel view the selling strategy editor.
 */
export const PerformanceToolbar: FC<PerformanceToolbarProps> = (props) => {
  const { teammateAgentId, view, funnelView } = props;
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const isFunnelUiWritable = checkFunnelUiWritable(
    useFeatureFlagVariant(FeatureFlagKey.VoiceAgentFunnelUi),
  );
  const isEditorOffered = view === "funnel" && isFunnelUiWritable;
  // Shared with the Funnel view's own queries through the cache.
  const { data: funnels } = useVoiceAgentFunnelsQuery(
    isEditorOffered ? teammateAgentId : skipToken,
  );
  // In v1 an agent has at most one funnel.
  const funnelId = funnels?.[0]?.id;
  const { data: funnel } = useFunnelQuery(funnelId ?? skipToken);
  const editableFunnel =
    isEditorOffered &&
    funnel?.induction_status === FunnelInductionStatus.Complete
      ? funnel
      : undefined;

  if (!funnelView.isShown && !editableFunnel) {
    return null;
  }
  return (
    <div className="flex items-center justify-between gap-4 px-4 pt-3 sm:px-6 sm:pt-4">
      {funnelView.isShown ? (
        <SegmentedControl
          aria-label="Performance views"
          size={Size.SMALL}
          options={[
            { id: "funnel", label: "Funnel" },
            { id: "analytics", label: "Analytics" },
          ]}
          value={view}
          onChange={(nextView) =>
            navigate(
              nextView === "funnel"
                ? appPaths.teammateAgentFunnel.build({ teammateAgentId })
                : appPaths.teammateAgentOverview.build({ teammateAgentId }),
            )
          }
        />
      ) : (
        <span />
      )}
      {editableFunnel ? (
        <>
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            icon={<IconSettings className="size-4" />}
            onClick={() => setIsSettingsOpen(true)}
          >
            Edit selling strategy
          </Button>
          <FunnelSettingsModal
            funnel={editableFunnel}
            isOpen={isSettingsOpen}
            onOpenChange={setIsSettingsOpen}
          />
        </>
      ) : null}
    </div>
  );
};
